import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Checkbox } from 'antd';
import axios from 'axios';
import LmHeader from '../components/LmHeader.jsx';
import LmFooter from '../components/LmFooter.jsx';
import PublicProductCatalog from '../components/PublicProductCatalog.jsx';

const Main = () => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'not_found', 'has_owner', 'available'
  const [existingClientEmail, setExistingClientEmail] = useState(''); // Email для существующего клиента
  const [email, setEmail] = useState(''); // Email для нового клиента
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeOffer, setAgreeOffer] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [blockRemainingSec, setBlockRemainingSec] = useState(null);

  // Живой таймер блокировки rate-limit (считаем от remainingSeconds с сервера)
  useEffect(() => {
    if (modalType !== 'rate_limited') {
      setBlockRemainingSec(null);
      return undefined;
    }

    const initial = verificationResult?.remainingSeconds
      || (verificationResult?.remainingMinutes || 15) * 60;

    setBlockRemainingSec(initial);
    const id = setInterval(() => {
      setBlockRemainingSec((prev) => {
        if (prev == null) return initial;
        return Math.max(0, prev - 1);
      });
    }, 1000);
    return () => clearInterval(id);
  }, [modalType, verificationResult?.remainingSeconds, verificationResult?.remainingMinutes]);

  const handleVerification = async () => {
    if (!verificationCode.trim()) return;

    setLoading(true);
    console.log('🔍 [FRONTEND] Начинаем проверку кода:', verificationCode);

    try {
      const response = await axios.post('https://limitverify.ru:5000/api/verification/check', {
        code: verificationCode.trim()
      });

      console.log('✅ [FRONTEND] Результат проверки:', response.data);
      setVerificationResult(response.data);

      if (response.data.status === 'not_found') {
        setModalType('not_found');
        setShowModal(true);
      } else if (response.data.status === 'has_owner') {
        setModalType('has_owner');
        setShowModal(true);
      } else if (response.data.status === 'available') {
        setModalType('available');
        setShowModal(true);
      }

    } catch (error) {
      console.error('❌ [FRONTEND] Ошибка проверки:', error);

      if (error.response?.status === 429) {
        const data = error.response.data || {};
        const seconds = data.remainingSeconds
          || (data.retryAfter ? Number(data.retryAfter) : null)
          || (data.remainingMinutes ? data.remainingMinutes * 60 : 15 * 60);
        const minutes = Math.max(1, Math.ceil(seconds / 60));

        setVerificationResult({
          isValid: false,
          message: data.message || `Вы заблокированы за превышение лимита запросов на ${minutes} минут.`,
          isRateLimited: true,
          remainingMinutes: minutes,
          remainingSeconds: seconds
        });
        setModalType('rate_limited');
      } else {
        setVerificationResult({
          isValid: false,
          message: 'Ошибка при проверке кода. Попробуйте еще раз.'
        });
        setModalType('error');
      }
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClientEnter = () => {
    navigate('/enter');
  };

  const handleRegisterToExistingClient = async () => {
    if (!existingClientEmail.trim()) return;

    setLoading(true);
    console.log('📝 [FRONTEND] Регистрируем изделие за клиентом:', existingClientEmail);

    try {
      const response = await axios.post('https://limitverify.ru:5000/api/verification/register', {
        productId: verificationResult.productInfo.id,
        email: existingClientEmail.trim()
      });

      console.log('✅ [FRONTEND] Регистрация успешна:', response.data);
      setShowModal(false);
      setVerificationResult(null);
      setVerificationCode('');
      setExistingClientEmail('');

      // Показываем успешное сообщение
      alert('Изделие успешно зарегистрировано! Теперь вы можете войти в кабинет.');

    } catch (error) {
      console.error('❌ [FRONTEND] Ошибка регистрации:', error);

      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfter || 900;
        const minutes = Math.ceil(retryAfter / 60);
        alert(`Вы заблокированы за превышение лимита запросов на ${minutes} минут.`);
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Ошибка при регистрации изделия. Попробуйте еще раз.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClientAndRegister = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      alert('Заполните все поля');
      return;
    }

    if (password !== confirmPassword) {
      alert('Пароли не совпадают');
      return;
    }

    setLoading(true);
    console.log('👤 [FRONTEND] Создаем кабинет и регистрируем изделие');

    try {
      const response = await axios.post('https://limitverify.ru:5000/api/verification/create-client', {
        productId: verificationResult.productInfo.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password: password,
        agreeOffer: agreeOffer,
        agreePrivacy: agreePrivacy
      });

      console.log('✅ [FRONTEND] Кабинет создан:', response.data);

      // Сохраняем токен и данные клиента
      localStorage.setItem('clientToken', response.data.token);
      localStorage.setItem('clientData', JSON.stringify(response.data.clientInfo));

      setShowModal(false);
      setVerificationResult(null);
      setVerificationCode('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');

      // Перенаправляем в кабинет
      navigate('/cabinet');

    } catch (error) {
      console.error('❌ [FRONTEND] Ошибка создания кабинета:', error);

      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfter || 900;
        const minutes = Math.ceil(retryAfter / 60);
        alert(`Вы заблокированы за превышение лимита запросов на ${minutes} минут.`);
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Ошибка при создании кабинета. Попробуйте еще раз.');
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setVerificationResult(null);
    setExistingClientEmail('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
  };

  const product = verificationResult?.productInfo;

  return (
    <div className="lm-page">
      <LmHeader actions={[{ label: 'Личный кабинет', onClick: handleClientEnter }]} />

      <main className="lm-content lm-content--center">
        <div className="lm-auth">
          <section className="lm-section">
            <div className="lm-auth__head">
              <h2 className="lm-h2">Проверка подлинности</h2>
            </div>

            <div className="lm-field">
              <input
                type="text"
                className="lm-field__input"
                placeholder="Введите идентификационный номер"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleVerification()}
              />
            </div>

            <button
              type="button"
              className="lm-btn lm-btn--block"
              onClick={handleVerification}
              disabled={!verificationCode.trim() || loading}
            >
              {loading ? 'Проверяем...' : 'Проверить'}
            </button>

            <button
              type="button"
              className="lm-link lm-link--accent"
              style={{ alignSelf: 'center' }}
              onClick={handleClientEnter}
            >
              Войти в личный кабинет клиента
            </button>
          </section>
        </div>
      </main>

      <PublicProductCatalog />

      <LmFooter />

      {/* Модальные окна */}
      {showModal && (
        <div className="lm-modal-overlay">
          <div className="lm-modal">
            <button type="button" className="lm-modal__close" onClick={closeModal} aria-label="Закрыть">
              ✕
            </button>

            {/* Заголовок */}
            {modalType === 'not_found' && <h3 className="lm-modal__title">Изделие не найдено</h3>}
            {modalType === 'has_owner' && <h3 className="lm-modal__title">Изделие уже зарегистрировано</h3>}
            {modalType === 'available' && <h3 className="lm-modal__title">Изделие доступно для регистрации</h3>}
            {modalType === 'error' && <h3 className="lm-modal__title">Ошибка проверки</h3>}
            {modalType === 'rate_limited' && <h3 className="lm-modal__title">Превышен лимит запросов</h3>}

            {/* Содержимое */}
            {modalType === 'not_found' && (
              <div className="lm-text-3 lm-muted" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <span>
                  Изделие с кодом <strong className="lm-accent">{verificationCode}</strong> не найдено в нашей базе данных.
                </span>
                <span>Возможно, это подделка или код введён неверно. Проверьте правильность ввода кода.</span>
              </div>
            )}

            {modalType === 'has_owner' && (
              <div className="lm-text-3 lm-muted" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <span>
                  Изделие <strong className="lm-accent">{product?.name}</strong> является оригинальным, но уже зарегистрировано за владельцем.
                </span>
                <span>Если это ваше изделие, войдите в личный кабинет для просмотра деталей.</span>
              </div>
            )}

            {modalType === 'available' && (
              <>
                {/* Информация об изделии */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    padding: 16,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-control)'
                  }}
                >
                  <div
                    style={{
                      width: 200,
                      height: 200,
                      alignSelf: 'center',
                      background: 'var(--bg-page)',
                      border: '1px solid var(--border-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {product?.mainPhoto ? (
                      <img
                        src={product.mainPhoto}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span className="lm-muted">нет фото</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <ProductRow label="Название" value={product?.name} />
                    <ProductRow label="ID" value={product?.identificationNumber} />
                    {product?.serialNumber && <ProductRow label="Серийный" value={product.serialNumber} />}
                    {product?.features && <ProductRow label="Особенности" value={product.features} />}
                    {product?.description && <ProductRow label="Описание" value={product.description} />}
                  </div>
                </div>

                <span className="lm-descriptor lm-muted" style={{ textAlign: 'center' }}>
                  Выберите способ регистрации
                </span>

                {/* Существующий клиент */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <h4 className="lm-h4">У меня уже есть кабинет</h4>
                  <div className="lm-field">
                    <span className="lm-field__label">Email</span>
                    <input
                      type="email"
                      className="lm-field__input"
                      placeholder="Введите ваш email"
                      value={existingClientEmail}
                      onChange={(e) => setExistingClientEmail(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="lm-btn lm-btn--block"
                    onClick={handleRegisterToExistingClient}
                    disabled={!existingClientEmail.trim() || loading}
                  >
                    {loading ? 'Регистрируем...' : 'Зарегистрировать изделие'}
                  </button>
                </div>

                {/* Новый клиент */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <h4 className="lm-h4">Создать новый кабинет</h4>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div className="lm-field" style={{ flex: 1, minWidth: 140 }}>
                      <span className="lm-field__label">Имя</span>
                      <input
                        type="text"
                        className="lm-field__input"
                        placeholder="Имя"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div className="lm-field" style={{ flex: 1, minWidth: 140 }}>
                      <span className="lm-field__label">Фамилия</span>
                      <input
                        type="text"
                        className="lm-field__input"
                        placeholder="Фамилия"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="lm-field">
                    <span className="lm-field__label">Email</span>
                    <input
                      type="email"
                      className="lm-field__input"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="lm-field">
                    <span className="lm-field__label">Пароль</span>
                    <input
                      type="password"
                      className="lm-field__input"
                      placeholder="Пароль"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="lm-field">
                    <span className="lm-field__label">Подтвердите пароль</span>
                    <input
                      type="password"
                      className="lm-field__input"
                      placeholder="Подтвердите пароль"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0' }}>
                    <Checkbox checked={agreeOffer} onChange={(e) => setAgreeOffer(e.target.checked)}>
                      <span className="lm-text-3">
                        Согласен с{' '}
                        <Link to="/offer-agreement" target="_blank" className="lm-accent">
                          договором-офертой
                        </Link>
                      </span>
                    </Checkbox>
                    <Checkbox checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)}>
                      <span className="lm-text-3">
                        Согласен с{' '}
                        <Link to="/privacy-policy" target="_blank" className="lm-accent">
                          политикой конфиденциальности
                        </Link>
                      </span>
                    </Checkbox>
                  </div>

                  <button
                    type="button"
                    className="lm-btn lm-btn--block"
                    onClick={handleCreateClientAndRegister}
                    disabled={
                      !firstName.trim() ||
                      !lastName.trim() ||
                      !email.trim() ||
                      !password.trim() ||
                      !agreeOffer ||
                      !agreePrivacy ||
                      loading
                    }
                  >
                    {loading ? 'Создаём кабинет...' : 'Создать кабинет и зарегистрировать изделие'}
                  </button>
                </div>
              </>
            )}

            {modalType === 'error' && (
              <p className="lm-text-3 lm-muted" style={{ margin: 0 }}>
                {verificationResult?.message || 'Произошла ошибка при проверке кода.'}
              </p>
            )}

            {modalType === 'rate_limited' && (
              <div className="lm-text-3 lm-muted" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{
                    padding: 16,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-control)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  <span className="lm-text-2" style={{ color: 'var(--text-primary)' }}>
                    Вы заблокированы за превышение лимита запросов
                  </span>
                  {blockRemainingSec != null && blockRemainingSec > 0 && (
                    <span className="lm-accent" style={{ textAlign: 'center' }}>
                      Осталось: {Math.floor(blockRemainingSec / 60)} мин {String(blockRemainingSec % 60).padStart(2, '0')} сек
                    </span>
                  )}
                  {blockRemainingSec === 0 && (
                    <span className="lm-accent" style={{ textAlign: 'center' }}>
                      Блокировка снята — можно закрыть окно и повторить проверку
                    </span>
                  )}
                </div>
                <span>Для защиты от злоупотреблений мы ограничиваем количество запросов:</span>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Максимум 3 запроса в минуту</li>
                  <li>При превышении — блокировка на 15 минут</li>
                  <li>Попробуйте позже или используйте другой браузер</li>
                </ul>
              </div>
            )}

            {/* Кнопки управления */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {modalType === 'not_found' && (
                <button type="button" className="lm-btn" onClick={closeModal}>
                  Понятно
                </button>
              )}

              {modalType === 'has_owner' && (
                <>
                  <button type="button" className="lm-btn lm-btn--danger" onClick={closeModal}>
                    Закрыть
                  </button>
                  <button type="button" className="lm-btn" onClick={handleClientEnter}>
                    Войти в кабинет
                  </button>
                </>
              )}

              {modalType === 'available' && (
                <button type="button" className="lm-btn lm-btn--danger" onClick={closeModal}>
                  Отмена
                </button>
              )}

              {modalType === 'error' && (
                <button type="button" className="lm-btn" onClick={closeModal}>
                  Понятно
                </button>
              )}

              {modalType === 'rate_limited' && (
                <button type="button" className="lm-btn" onClick={closeModal}>
                  Понятно
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductRow = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      gap: 8,
      padding: '10px 12px',
      border: '1px solid var(--border-color)',
      background: 'var(--bg-page)'
    }}
  >
    <span className="lm-descriptor lm-muted" style={{ minWidth: 90 }}>
      {label}
    </span>
    <span className="lm-text-3" style={{ color: 'var(--text-primary)' }}>
      {value}
    </span>
  </div>
);

export default Main;
